import { useMemo } from 'react';
import { isNil } from 'lodash';
import type { TFunction } from 'react-i18next';
// Import Types
import type { DataTableColumn } from 'types';
import type { WorkflowBlockDisplayInfo } from 'context/cjWorkflowBlocksContext';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Components
import Typography from 'components/common/Typography';
// Import Services
import { Helpers } from 'services';
// Import Constants
import { l, BonusTypesLabels, BonusTypesIcons } from 'constants/common';
// Import Enums
import { FormatNumberActionEnum } from 'enums';
// Import Styles
import styles from './cjBonusReceivedPlayersSection.module.scss';

const { formatNumber } = Helpers;

export const useBonusBlocksReportColumns = (
    t: TFunction,
    getBlockInfo: (blockName: string) => WorkflowBlockDisplayInfo,
): { columns: DataTableColumn[] } => {
    return useMemo(
        () => ({
            columns: [
                {
                    text: t(l.BlockInfo),
                    dataKey: 'BlockName',
                    sortable: false,
                    colRenderer: (blockName) => {
                        const { isRemoved, displayName, typeLabelKey } = getBlockInfo(blockName);

                        if (isRemoved) {
                            return (
                                <div className={styles.blockInfoCell}>
                                    <span>{t(l.BlockRemoved)}</span>
                                    <Icon type="bc-icon-disable" />
                                </div>
                            );
                        }

                        const typeText = typeLabelKey ? ` | ${t(typeLabelKey)}` : '';

                        return (
                            <div className={styles.blockInfoCell}>
                                <Typography
                                    text={`${displayName}${typeText}`}
                                    className={styles.blockInfoText}
                                    withEllipsis={true}
                                />
                            </div>
                        );
                    },
                },
                {
                    text: t(l.BonusId),
                    dataKey: 'AwardId',
                    sortable: false,
                },
                {
                    text: t(l.BonusName),
                    dataKey: 'AwardName',
                    sortable: false,
                },
                {
                    text: t(l.BonusType),
                    dataKey: 'AwardType',
                    sortable: false,
                    colRenderer: (awardType) => (
                        <div className={styles.bonusTypeCell}>
                            {!isNil(BonusTypesIcons[awardType]) && <Icon type={BonusTypesIcons[awardType]} />}
                            <span>{isNil(BonusTypesLabels[awardType]) ? '' : t(BonusTypesLabels[awardType])}</span>
                        </div>
                    ),
                },
                {
                    text: t(l.SuccessSentCount),
                    dataKey: 'Count',
                    sortable: false,
                    colRenderer: (count) =>
                        isNil(count)
                            ? t(l.NA)
                            : formatNumber(count, {
                                  type: FormatNumberActionEnum.DecimalPrecision,
                                  precision: 0,
                              }),
                },
                {
                    text: t(l.SuccessSentAmount),
                    dataKey: 'Amount',
                    sortable: false,
                    colRenderer: (amount, _index, { data }) => {
                        if (isNil(amount)) {
                            return t(l.NA);
                        }
                        const formattedAmount = formatNumber(amount, {
                            type: FormatNumberActionEnum.DecimalPrecision,
                            precision: 2,
                        });
                        return `${formattedAmount} ${data?.CurrencyId ?? ''}`.trim();
                    },
                },
            ],
        }),
        [t, getBlockInfo],
    );
};
